// src/components/DeliverySearch.js
// MODIFICADO: Se mejora el mensaje de error para que sea más amigable
// y se ajusta el tipo de alerta a 'warning'.

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Row, Col, Form, Button, DatePicker, Input, Table, message, Tag, Checkbox, Space, Tooltip, Modal, Select, Alert } from 'antd';
import { SearchOutlined, EditOutlined, DeleteOutlined, FileAddOutlined, SendOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { deliveryService, catalogService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import moment from 'moment';

const { Option } = Select;

const DeliverySearch = ({ onSelectDelivery }) => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [searchError, setSearchError] = useState(null); // Estado para errores de búsqueda
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);

  // Paginación server-side: el backend pagina contra SAP, la tabla refleja el total real.
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const permissions = user?.role?.profile?.permissions;
  const canViewPage = permissions?.pages?.viewDeliveries;
  const canSearch = permissions?.actions?.deliveries?.canSearch;
  const canIncludeClosed = permissions?.actions?.deliveries?.canIncludeClosed;

  // override: { current, pageSize } cuando viene de Table.onChange o de submit
  // (que fuerza current=1). Si no se pasa, usa el estado actual.
  const searchDeliveries = useCallback(async (override = null) => {
    try {
      setLoading(true);
      setSearchError(null);
      const currentFormValues = form.getFieldsValue(true);
      const params = {};
      if (currentFormValues.docNum) params.docNum = currentFormValues.docNum;
      if (currentFormValues.cardName) params.cardName = currentFormValues.cardName;
      if (currentFormValues.packingListNumber) params.packingListNumber = currentFormValues.packingListNumber;
      if (currentFormValues.dateFrom && typeof currentFormValues.dateFrom.format === 'function') {
        params.dateFrom = currentFormValues.dateFrom.clone().format('YYYY-MM-DD');
      }
      if (currentFormValues.dateTo && typeof currentFormValues.dateTo.format === 'function') {
        params.dateTo = currentFormValues.dateTo.clone().format('YYYY-MM-DD');
      }
      params.includeClosed = currentFormValues.includeClosed || false;

      const targetPage     = override?.current  ?? pagination.current;
      const targetPageSize = override?.pageSize ?? pagination.pageSize;
      params.page = targetPage;
      params.pageSize = targetPageSize;

      const response = await deliveryService.getDeliveries(params);

      if (response.data.success) {
        const items = response.data.data || [];
        const total = response.data.pagination?.total ?? items.length;
        setDeliveries(items);
        setPagination({ current: targetPage, pageSize: targetPageSize, total });
        const activeFiltersApplied = ['docNum','cardName','packingListNumber','dateFrom','dateTo']
          .some(key => params[key] !== undefined && params[key] !== null && params[key] !== '');
        if (items.length === 0 && activeFiltersApplied) {
          message.info('No se encontraron entregas con los criterios seleccionados');
        }
      }
    } catch (error) {
      console.error('Error al buscar entregas:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Ocurrió un error inesperado al conectar con el servidor.';
      setSearchError(errorMessage);
      setDeliveries([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, [form, pagination.current, pagination.pageSize]);

  useEffect(() => {
    if (!canViewPage) return;
    // Carga inicial: empieza en página 1. Si vienen filtros por query string
    // (ej. desde el GlobalSearchModal), los precargamos en el form antes de
    // disparar la búsqueda.
    const cardName = searchParams.get('cardName');
    const docNum = searchParams.get('docNum');
    const initialValues = {};
    if (cardName) initialValues.cardName = cardName;
    if (docNum)   initialValues.docNum = docNum;
    if (Object.keys(initialValues).length > 0) {
        form.setFieldsValue(initialValues);
    }
    searchDeliveries({ current: 1, pageSize: 20 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewPage, searchParams]);

  useEffect(() => {
    if (isModalVisible) {
        setReasonsLoading(true);
        catalogService.getDeletionReasons()
            .then(response => {
                if (response.data.success) {
                    setReasons(response.data.data);
                } else {
                    message.error('No se pudieron cargar los motivos de eliminación.');
                }
            })
            .catch(err => {
                console.error("Error al cargar motivos:", err);
                message.error('Error al cargar los motivos.');
            })
            .finally(() => {
                setReasonsLoading(false);
            });
    }
  }, [isModalVisible]);


  const showDeletionModal = (record, isDirectDelete) => {
    setRecordToDelete({ ...record, isDirectDelete });
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    modalForm.resetFields();
    setRecordToDelete(null);
  };

  const handleModalOk = async () => {
    try {
        const values = await modalForm.validateFields();
        const motivoSeleccionado = values.reason;

        setIsSubmitting(true);
        if (recordToDelete.isDirectDelete) {
            await deliveryService.deletePackingList(recordToDelete.DocEntry, motivoSeleccionado);
            message.success('Listado de empaque eliminado directamente.');
        } else {
            await deliveryService.requestDeletion(recordToDelete.DocEntry, motivoSeleccionado);
            message.success('Solicitud de eliminación enviada correctamente.');
        }
        handleModalCancel();
        searchDeliveries();
    } catch (error) {
        if (error.response) {
            console.error('Error en la operación de eliminación:', error);
            const errorMessage = error.response?.data?.message || 'Ocurrió un error.';
            message.error(errorMessage);
        }
    } finally {
        setIsSubmitting(false);
    }
  };


  const columns = useMemo(() => [
    { title: 'No. Doc', dataIndex: 'DocNum', key: 'docNum', sorter: (a, b) => a.DocNum - b.DocNum },
    { title: 'Fecha', dataIndex: 'DocDate', key: 'docDate', render: (text) => moment.utc(text).format('DD/MM/YYYY'), sorter: (a, b) => moment(a.DocDate).unix() - moment(b.DocDate).unix() },
    { title: 'Nombre de Cliente', dataIndex: 'CardName', key: 'cardName', sorter: (a, b) => a.CardName.localeCompare(b.CardName) },
    { title: 'Código Cliente', dataIndex: 'CardCode', key: 'cardCode' },
    { title: 'Estado', dataIndex: 'DocumentStatus', key: 'documentStatus',
      render: (text) => {
        const status = text === 'bost_Open' ? 'Abierto' : text === 'bost_Close' ? 'Cerrado' : text;
        const color = text === 'bost_Open' ? 'green' : text === 'bost_Close' ? 'red' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    { title: 'Folio Listado', dataIndex: 'packingListNumber', key: 'packingListNumber', sorter: (a, b) => {
        const folioA = a.packingListNumber || '';
        const folioB = b.packingListNumber || '';
        return folioA.localeCompare(folioB);
      },
      render: (text, record) => (
        <Space>
            {text ? <Tag color="blue">{text}</Tag> : <span>-</span>}
            {record.deletionStatus === 'pendiente_eliminacion' && (
                <Tooltip title="Este listado está pendiente de eliminación.">
                    <Tag icon={<ClockCircleOutlined />} color="orange">Pendiente</Tag>
                </Tooltip>
            )}
        </Space>
      ) 
    },
    { title: 'Acciones', key: 'action',
      render: (_, record) => {
        if (record.deletionStatus === 'pendiente_eliminacion') {
            return (
                <Tooltip title="Esta solicitud está pendiente de aprobación y no puede ser modificada.">
                    <Tag icon={<ClockCircleOutlined />} color="warning">
                        En Proceso
                    </Tag>
                </Tooltip>
            );
        }

        const deliveryPerms = permissions?.actions?.deliveries;
        const isOpen = record.DocumentStatus === 'bost_Open';
        const permsForStatus = isOpen ? deliveryPerms?.onOpen : deliveryPerms?.onClosed;

        const canCreate = permsForStatus?.canCreatePackingList;
        const canEdit = permsForStatus?.canEditPackingList;
        const canDeleteDirectly = permsForStatus?.canDeletePackingList;
        const canRequestDeletion = permsForStatus?.canRequestPackingListDeletion;

        return (
          <Space>
            {record.packingListNumber ? (
              canEdit && (
                <Tooltip title="Editar LE">
                    <Button 
                        type="primary" 
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => onSelectDelivery(record)}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    />
                </Tooltip>
              )
            ) : (
              canCreate && (
                <Tooltip title="Crear Empaque">
                    <Button 
                        type="primary" 
                        size="small"
                        icon={<FileAddOutlined />}
                        onClick={() => onSelectDelivery(record)}
                    />
                </Tooltip>
              )
            )}
            {record.packingListNumber && (
                <>
                    {canDeleteDirectly ? (
                        <Tooltip title="Eliminar Directamente">
                            <Button 
                                danger 
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={() => showDeletionModal(record, true)}
                            />
                        </Tooltip>
                    ) : canRequestDeletion && (
                        <Tooltip title="Solicitar Eliminación">
                            <Button 
                                danger
                                type="primary"
                                size="small"
                                icon={<SendOutlined />}
                                style={{ backgroundColor: '#ff7a45', borderColor: '#ff7a45' }}
                                onClick={() => showDeletionModal(record, false)}
                            />
                        </Tooltip>
                    )}
                </>
            )}
          </Space>
        );
      },
    },
  ], [permissions, onSelectDelivery]); 

  const handleIncludeClosedChange = () => {
    setTimeout(() => {
        form.submit();
    }, 0);
  };

  const handleCardNameInputChange = (e) => {
    const { value } = e.target;
    form.setFieldsValue({ cardName: value.toUpperCase() });
  };

  // Submit del form (botón Buscar o cambio de "Incluir Cerradas"): fuerza página 1
  // y descarta los `values` que AntD pasa por argumento (searchDeliveries lee del form).
  const handleFormSubmit = () => {
    searchDeliveries({ current: 1, pageSize: pagination.pageSize });
  };
  
  if (!canViewPage) {
      return <Card title="Búsqueda de Entregas"><p>No tiene permiso para acceder a este módulo.</p></Card>;
  }

  return (
    <>
      <Card title="Búsqueda de Entregas" variant="borderless">
        {searchError && (
            <Alert
                message="Atención"
                description={searchError}
                type="warning"
                showIcon
                closable
                onClose={() => setSearchError(null)}
                style={{ marginBottom: 16 }}
            />
        )}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          initialValues={{
            includeClosed: false,
            dateFrom: null, 
            dateTo: null,   
          }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12} lg={4}>
              <Form.Item label="Número de Entrega" name="docNum">
                <Input placeholder="Ingrese el número SAP" disabled={!canSearch} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Form.Item label="Folio Listado" name="packingListNumber">
                <Input placeholder="Folio: LP-..., LP-B-..." disabled={!canSearch} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item label="Nombre de Cliente" name="cardName">
                <Input 
                  placeholder="Nombre SAP del cliente" 
                  onChange={handleCardNameInputChange}
                  disabled={!canSearch} 
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Form.Item label="Fecha desde" name="dateFrom">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} disabled={!canSearch} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Form.Item label="Fecha hasta" name="dateTo">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} disabled={!canSearch} />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end" align="middle" gutter={16}>
            <Col>
              {canIncludeClosed && (
                <Form.Item name="includeClosed" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Checkbox onChange={handleIncludeClosedChange}>Incluir Cerradas</Checkbox>
                </Form.Item>
              )}
            </Col>
            <Col>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SearchOutlined />} 
                loading={loading}
                disabled={!canSearch}
              >
                Buscar
              </Button>
            </Col>
          </Row>
        </Form>

        <Table
          columns={columns}
          dataSource={deliveries}
          rowKey="DocEntry"
          loading={loading}
          style={{ marginTop: 16 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (total) => `${total} entregas en total`,
          }}
          onChange={(paginationConfig) => {
            searchDeliveries({
              current: paginationConfig.current,
              pageSize: paginationConfig.pageSize,
            });
          }}
          rowClassName={record => record.packingListNumber ? 'table-row-packed' : ''}
        />
      </Card>
      <Modal
        title={recordToDelete?.isDirectDelete ? "Eliminar Listado Directamente" : "Solicitar Eliminación de Listado"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={isSubmitting}
        okText={recordToDelete?.isDirectDelete ? "Eliminar" : "Enviar Solicitud"}
        cancelText="Cancelar"
        destroyOnClose
      >
        <p>Está a punto de {recordToDelete?.isDirectDelete ? 'eliminar directamente' : 'solicitar la eliminación para'} el listado <strong>{recordToDelete?.packingListNumber}</strong> (Doc. {recordToDelete?.DocNum}).</p>
        
        <Form form={modalForm} layout="vertical" name="request_deletion_form" style={{ marginTop: 24 }}>
            <Form.Item
                name="reason"
                label="Por favor, seleccione el motivo de esta acción. Es obligatorio."
                rules={[{ required: true, message: 'Debe seleccionar un motivo.' }]}
            >
                <Select
                    placeholder="Seleccione un motivo de la lista"
                    loading={reasonsLoading}
                    showSearch
                    optionFilterProp="children"
                >
                    {reasons.map(reason => (
                        <Option key={reason._id} value={reason.reason}>
                            {reason.reason}
                        </Option>
                    ))}
                </Select>
            </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default DeliverySearch;
